import { Signal, computed, effect, inject, untracked } from '@angular/core'

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals'
import { isEqual } from 'lodash'

import { Observable, Subscription } from 'rxjs'

import { HarnessConfigService } from '@flowx/designer/modules/ai-builder/services/harness-config.service'
import { HarnessSessionLifetime } from '@flowx/designer/modules/ai-builder/services/harness-session.lifetime'
import { HarnessWriteTargetNavigator } from '@flowx/designer/modules/ai-builder/services/harness-write-target.navigator'
import { HarnessConversationDetailsStore } from '@flowx/designer/modules/ai-builder/store/harness-conversation-details.store'
import { HarnessFeedbackStore } from '@flowx/designer/modules/ai-builder/store/harness-feedback.store'
import { HarnessReviewStore } from '@flowx/designer/modules/ai-builder/store/harness-review.store'
import {
  acceptExecutionPreview,
  appendAnswers,
  appendUserMessage,
  applyCancel,
  applyFrame,
  applyRunCut,
  applyRunFailure,
  applyRunFrame,
  applyTurnFailure,
  initialHarnessRunState,
  initialHarnessSessionState,
  reconcileSessionRecord,
  restoreSession,
  withNotice,
} from '@flowx/designer/modules/ai-builder/store/harness-session.reducer'
import {
  HarnessWriteTarget,
  toWriteTarget,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  HarnessAnswerInput,
  HarnessArtefact,
  HarnessRunState,
  HarnessSessionState,
  artefactUnderReview,
  isPlanProposalSettled,
} from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import {
  FeedbackClientContext,
  HARNESS_ERROR_CODES,
  HarnessDecision,
  HarnessExecutionPreview,
  HarnessFeedbackVerdict,
  HarnessFrame,
  HarnessMessageFeedback,
  HarnessSessionRecord,
  HarnessSessionSnapshot,
  isPlanReviewType,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import {
  BriefDisplay,
  toBriefDisplay,
} from '@flowx/designer/modules/ai-builder/utils/brief-display.util'
import {
  HarnessCallScope,
  isPlanGate,
  planScopeFor,
  turnScopeFor,
} from '@flowx/designer/modules/ai-builder/utils/call-scope.util'
import {
  harnessErrorCode,
  harnessErrorDetail,
  harnessErrorHttpStatus,
  harnessErrorMessage,
  harnessErrorReason,
} from '@flowx/designer/modules/ai-builder/utils/harness-error.util'
import { toQuestionDisplay } from '@flowx/designer/modules/ai-builder/utils/interrupt-display.util'
import { withMessageIds } from '@flowx/designer/modules/ai-builder/utils/message-ids.util'
import { blockerCount } from '@flowx/designer/modules/ai-builder/utils/open-question.util'
import {
  PlanDisplay,
  toPlanStepIds,
  toSessionPlanDisplay,
} from '@flowx/designer/modules/ai-builder/utils/plan-display.util'
import { toStoryPlanView } from '@flowx/designer/modules/ai-builder/utils/story-plan/story-display.util'
import { THINKING } from '@flowx/designer/modules/ai-builder/utils/text.util'
import { retryPrompt } from '@flowx/designer/modules/ai-builder/utils/transcript-turns.util'
import { HarnessApi } from '@flowx/designer/shared/api/harness.api'

/** The page context and pending conversation read beside the transcript and canonical run fold.
 * Staging, owner-read details and execution-local choices each have their own store. */
type HarnessScopeState = HarnessRunState & {
  /** The conversation being read to be put on screen — picked from the list, or the tab's own
   * coming back after a reload. Until it lands the rail says so, rather
   * than offering to start a new one. */
  openingSessionId: string | null
}

const initialScopeState: HarnessScopeState = {
  ...initialHarnessRunState,
  openingSessionId: null,
}

/** The builder's conversation with the harness, folded through the pure reducer. Root-provided
 * and owned by the maker, not by screen — the rail, the plan view and the hold read one session,
 * and toggling the sparkles off keeps it. */
export const HarnessSessionStore = signalStore(
  { providedIn: 'root' },
  withState<HarnessSessionState>(initialHarnessSessionState),
  withState<HarnessScopeState>(initialScopeState),
  withComputed(
    ({
      interrupt,
      latestBrief,
      latestPlan,
      storyPlanSnapshot,
      storyPlanProposal,
      runningTools,
      streamState,
      transcript,
      record,
    }) => {
      const config = inject(HarnessConfigService)
      // By value: each lifecycle frame brings a new record, and a new plan for the same steps
      // would close the step the maker has open in the spine.
      const plan = computed(
        (): PlanDisplay | null =>
          toSessionPlanDisplay(latestPlan(), storyPlanSnapshot(), record()?.plan ?? null),
        { equal: isEqual }
      )
      const storyPlanView = computed(() => {
        const snapshot = storyPlanSnapshot()
        return snapshot ? toStoryPlanView(snapshot) : null
      })
      const brief = computed((): BriefDisplay | null =>
        latestBrief() ? toBriefDisplay(latestBrief()) : null
      )
      const interruptType = computed(() => interrupt()?.type ?? null)
      const reviewedArtefact = computed(() => artefactUnderReview(interruptType()))
      /** Whichever document the open interrupt is asking about. Both carry open questions in the
       * same shape, so everything downstream reads the questions and not the artefact. */
      const reviewedDocument: Readonly<
        Record<HarnessArtefact, Signal<BriefDisplay | PlanDisplay | null>>
      > = { brief, plan }
      const underReview = computed(() => {
        const artefact = reviewedArtefact()

        return artefact ? reviewedDocument[artefact]() : null
      })
      /** Whether each document's review can be approved; `isApprovable` says why. */
      const approvableArtefact: Readonly<Record<HarnessArtefact, () => boolean>> = {
        brief: () => !brief()?.hardFailures.length,
        plan: () => false,
      }
      const planRecord = computed(() => record()?.plan ?? null)
      /** Every step the plan holds, a parked revision folded in: what progress is counted on. */
      const planStepIds = computed(() => toPlanStepIds(planRecord()), { equal: isEqual })
      const execution = computed(() => planRecord()?.execution ?? null)
      const isStreaming = computed(() => streamState() === 'streaming')
      /**
       * A plan's proposal interrupt whose go-ahead was already given by the execution controls —
       * the record is approved, the plan ran, or finished. The harness admits a new message over
       * it (D14), so the composer starts a turn rather than answering a proposal nobody can approve.
       */
      const settledProposal = computed(
        () =>
          isPlanReviewType(interruptType()) && isPlanProposalSettled(record(), storyPlanProposal())
      )

      /** The composer answers an interrupt when one is open, and starts a new turn otherwise. */
      const isAnsweringInterrupt = computed(
        () => !!interrupt() && streamState() === 'awaiting-answer' && !settledProposal()
      )

      return {
        /** Where the conversation writes, once a change was confirmed (conversation-owned). */
        writeTarget: computed((): HarnessWriteTarget | null => record()?.write_target ?? null),
        plan,
        storyPlanView,
        planUsesStories: computed(() => storyPlanSnapshot() !== null),
        brief,
        interruptType,
        isStreaming,
        isAnsweringInterrupt,
        /** The interrupt is open on the brief, so the document's handles steer rather than read back
         * as the record of what was decided. One source for it: the document and its interactive
         * sections are on this store already, to stage what the maker does with them. */
        isReviewingBrief: computed(
          () => isAnsweringInterrupt() && interruptType() === 'brief_review'
        ),
        /** What the agent is doing now: the tool in flight, "Thinking…" between calls, null when
         * idle. One signal, so every surface waiting on a turn says the same thing. Never a
         * finished tool's label — a caption blinking between a label and nothing reads as flicker. */
        activity: computed((): string | null =>
          streamState() === 'streaming' ? runningTools().at(-1)?.name || THINKING : null
        ),
        /** The turn stopped before its end and a new one is the way on. */
        canRetry: computed(() => streamState() === 'interrupted'),
        /** The review artefact that arrived last — what the Plan view opens on. */
        latestArtefact: computed(
          (): HarnessArtefact | null =>
            transcript()
              .filter((entry) => entry.type === 'artefact-marker')
              .map((entry) => (entry.type === 'artefact-marker' ? entry.artefact : null))
              .at(-1) ?? null
        ),
        /** Open questions the agent marked as blocking on the artefact under review. The switch
         * picks the document the interrupt is asking about — a brief is not a plan — and the count
         * itself is one rule, read from the questions rather than from either document. */
        blockers: computed(() => blockerCount(underReview()?.openQuestions ?? [])),
        /**
         * Judged on the OPEN interrupt, never on an artefact two turns old — and not on
         * `is_approvable`, which the harness derives from the blockers and so is false whenever a
         * blocking question is open. That is the common case, and exactly when the confirm-once
         * step below has to stay reachable; only `hard_failures` close the door.
         *
         * **A plan is never approved** — the harness refuses it (409); only the description is.
         */
        isApprovable: computed(() => {
          const artefact = reviewedArtefact()

          return artefact ? approvableArtefact[artefact]() : true
        }),

        /** The plan's durable record — null before a plan exists. */
        planRecord,
        planStepIds,
        /** The one run this plan has had, if any. */
        execution,
        /** Declining a specialist mutation is not abandoning the plan. Only that gate takes a
         * reject; a plan keeps its history through Abandon instead. */
        canReject: computed(() => interruptType() === 'specialist_review'),
        /** A plan's state never closes the conversation (D14). A turn still needs a project page,
         * so off one the composer says where to go instead. */
        offProjectPageReason: computed((): string | null =>
          config.routeContext()
            ? null
            : 'Open a project to talk to the agent — this conversation stays open while you move around.'
        ),
      }
    }
  ),
  withMethods(
    (
      store,
      client = inject(HarnessApi),
      config = inject(HarnessConfigService),
      navigator = inject(HarnessWriteTargetNavigator)
    ) => {
      // What the maker has staged on the document in front of them. Owned elsewhere — this store
      // only reads it on the way out and clears it — so it is a collaborator, not state.
      const review = inject(HarnessReviewStore)
      const feedback = inject(HarnessFeedbackStore)
      const conversationDetails = inject(HarnessConversationDetailsStore)
      const lifetime = inject(HarnessSessionLifetime)

      // Not in state: no view renders a subscription, and a signal would notify readers that
      // never read it.
      let turnStream: Subscription | null = null

      const dropTurnStream = (): void => {
        turnStream?.unsubscribe()
        turnStream = null
      }

      /** Where a call is made from (doc 37 N1). A turn is made from the page; a plan call —
       * answering a review, running, amending, abandoning — on the write target. */
      const callScope = (scopeFor: typeof planScopeFor): HarnessCallScope | undefined => {
        const record = store.record()

        return (
          scopeFor(
            record?.write_target ?? null,
            config.routeContext(),
            record?.last_context ?? null
          ) ?? undefined
        )
      }

      const turnScope = (): HarnessCallScope | undefined => callScope(turnScopeFor)

      const planCallScope = (): HarnessCallScope | undefined => callScope(planScopeFor)

      const fail = (error: unknown): void =>
        patchState(store, (state) =>
          applyTurnFailure(state, harnessErrorCode(error), harnessErrorMessage(error))
        )

      /**
       * What a session read tells the tab that the stream cannot: the ids the harness filed this
       * conversation's messages under, and whatever feedback is already on them. The SSE frames
       * carry no ids at all, so until this runs a finished turn has nothing to rate.
       */
      const applyConversationRead = (snapshot: HarnessSessionSnapshot): void => {
        feedback.reconcileRead(snapshot.feedback)
        conversationDetails.reconcileRead(snapshot.conversation)
        patchState(store, (state) => ({
          ...(snapshot.record && snapshot.record.revision > (state.record?.revision ?? 0)
            ? reconcileSessionRecord(state, snapshot.record)
            : state),
          transcript: withMessageIds(state.transcript, snapshot.record?.events ?? []),
        }))
      }

      /** Re-read what the owner read adds beyond streamed frames: message ids, feedback and
       * conversation standing. A failed reconciliation stays quiet, as it did after a turn. */
      const rereadConversation = async (): Promise<void> => {
        const sessionId = store.sessionId()
        const isLatestRequest = lifetime.latestRequestCheck()

        if (!sessionId) {
          return
        }

        try {
          const snapshot = await client.readConversation(sessionId)

          if (isLatestRequest()) {
            applyConversationRead(snapshot)
          }
        } catch {
          // Ids the maker cannot see are feedback they cannot leave — not a failure of the turn.
        }
      }

      /** Feedback writes belong to the leaf; the session only supplies the conversation id. */
      const changeFeedback = (
        messageId: string,
        next: HarnessMessageFeedback | null,
        send: (sessionId: string) => Promise<unknown>
      ): void => {
        const sessionId = store.sessionId()

        if (sessionId) {
          feedback.submitChange(messageId, next, () => send(sessionId))
        }
      }

      /** `onError` defaults to closing the turn as failed. A turn's own stream passes one that
       * reattaches when the connection is cut, since the turn runs on without it. */
      const consume = (
        frames: ReturnType<typeof client.streamTurn>,
        onError: (error: unknown) => void = fail
      ): void => {
        dropTurnStream()
        const isSameConversation = lifetime.sameConversationCheck()

        turnStream = frames.subscribe({
          next: (frame: HarnessFrame) => {
            if (!isSameConversation()) {
              return
            }
            patchState(store, (state) => applyFrame(state, frame))

            // The turn's messages become rateable once the owner read has their ids.
            if (frame.type === 'end') {
              void rereadConversation()
            }
          },
          error: (error: unknown) => {
            if (isSameConversation()) {
              onError(error)
            }
          },
          // A stream that completes while the state still says `streaming` never delivered its
          // `end` frame. The client already errors on that, so this should be unreachable — but a
          // spinner that never stops is the one outcome worth closing twice.
          complete: () =>
            patchState(store, (state) =>
              isSameConversation() && state.streamState === 'streaming'
                ? applyTurnFailure(
                    state,
                    HARNESS_ERROR_CODES.streamTruncated,
                    'The connection ended before the turn finished.'
                  )
                : state
            ),
        })
      }

      /** The session id, opening one if this is the first turn. Persisted at once: a reload before
       * the first frame should still find the interrupt it is waiting on. */
      const ensureSession = async (
        context: ReturnType<typeof client.captureMessageContext>
      ): Promise<string> => {
        const isLatestRequest = lifetime.latestRequestCheck()
        const existing = store.sessionId()

        if (existing) {
          return existing
        }

        const { session_id } = await client.openSession(context)

        if (isLatestRequest()) {
          patchState(store, { sessionId: session_id })
          rememberConversation(session_id)
        }

        return session_id
      }

      /**
       * Answer the open interrupt. `respond` covers a clarifying answer, feedback on a proposal and
       * structured steering — one wire call, and the agent decides which it was. The interrupt's id
       * travels with every answer; the harness refuses one that names no interrupt, or an interrupt answered
       * elsewhere, which is what stops a stale tab from deciding for the current one.
       *
       * `echo` is what the transcript records for the maker: their words as a "You" turn, or — for
       * what they settled without writing — check-lines under the turn that asked.
       */
      const resume = (
        decision: HarnessDecision,
        message?: string,
        echo: string | readonly HarnessAnswerInput[] | undefined = message,
        allowAdjustments?: boolean
      ): void => {
        const sessionId = store.sessionId()
        const interruptId = store.interrupt()?.interrupt_id
        // Read before the interrupt is cleared: answering a review acts on the plan, answering a
        // question or a read grant is a turn (doc 37 N1).
        const answerScope = isPlanGate(store.interrupt()?.type) ? planCallScope : turnScope
        if (!sessionId || store.isStreaming()) {
          return
        }
        // A turn can accompany a run (D14), but still needs somewhere to be made from.
        const scope = answerScope()

        if (!scope) {
          return
        }
        // A staged edit asks for a revision, never an approval or rejection.
        if (review.hasSteering() && decision !== 'respond') {
          return
        }

        // Read out before it is cleared, and cleared here rather than inside the review store:
        // whatever the maker staged rides this turn, so the wire payload and the settled lines
        // have to be taken while it is still there.
        const steering = review.hasSteering() ? review.payload() : undefined
        const settled = review.hasSteering() ? review.describe(store.brief()?.decisions ?? []) : []

        patchState(store, (state) => {
          const staged = settled.length ? appendAnswers(state, settled) : state

          return {
            preview: null,
            ...(typeof echo === 'string'
              ? appendUserMessage(staged, echo)
              : echo?.length
                ? appendAnswers(staged, echo)
                : staged),
            streamState: 'streaming' as const,
            interrupt: null,
            // Only a note on a brief asks for a revision of it, so no other answer makes the next
            // brief "on your note".
            briefRevisionPending:
              decision === 'respond' && state.interrupt?.type === 'brief_review',
          }
        })
        // The adjustments consent is the confirmation's, so only an approval spends it.
        if (decision === 'approve') {
          review.clear()
        } else {
          review.clearSteering()
        }

        // Built at the press, and again for a retry: the retry must name the target the refusal
        // just reported, not the one the first attempt had.
        const start = (): Observable<HarnessFrame> =>
          client.streamResume(
            sessionId,
            decision,
            message,
            steering,
            interruptId,
            client.captureMessageContext(answerScope() ?? scope),
            allowAdjustments
          )

        consume(start(), onRefusal(sessionId, start))
      }

      /**
       * A conversation-owned harness refuses a call it cannot take (409) before running anything.
       * Two refusals have a way on that is not the maker's to find:
       *
       * `target_headers_required` — a plan call named the wrong project: the conversation got a
       * target since this tab read it (another tab confirmed a change). The refusal carries the
       * target; the call is made again under it, once. Only a plan call is refused so (doc 37 N1):
       * a turn carries the page's headers.
       *
       * Anything else — a moved context, a stale decision — is shown as the harness said it, over the
       * session as it now stands: the optimistic answer came off the screen with the interrupt it
       * answered, and only a read puts that interrupt back.
       *
       * Every other failure is a cut or a broken stream, handled as it always was.
       */
      const onRefusal =
        (sessionId: string, retry?: () => Observable<HarnessFrame>) =>
        (error: unknown): void => {
          if (harnessErrorHttpStatus(error) !== 409) {
            reattachOnCut(sessionId)(error)

            return
          }

          const target =
            harnessErrorReason(error) === 'target_headers_required'
              ? toWriteTarget(harnessErrorDetail(error)?.['write_target'])
              : null
          const record = store.record()

          if (target && record && retry) {
            patchState(store, { record: { ...record, write_target: target } })
            consume(retry(), onRefusal(sessionId))

            return
          }

          rereadAfterRefusal(sessionId, error)
        }

      const rereadAfterRefusal = (sessionId: string, error: unknown): void => {
        const isLatestRequest = lifetime.latestRequestCheck()

        dropTurnStream()
        void client
          .readConversation(sessionId)
          .then((snapshot) => {
            if (isLatestRequest() && store.sessionId() === sessionId && snapshot.record) {
              show(store.appVersionId(), sessionId, snapshot)
              patchState(store, (state) => withNotice(state, 'error', harnessErrorMessage(error)))
            } else if (isLatestRequest()) {
              fail(error)
            }
          })
          .catch(() => {
            if (isLatestRequest()) {
              fail(error)
            }
          })
      }

      /** A cut is often the network itself going away, and a read sent at that instant fails with
       * it. So the read waits until the browser is online, and is retried a few times for a blip the
       * browser does not report.
       *
       * ponytail: fixed attempts and delay; move to backoff if longer outages must be ridden out. */
      const readBack = async (sessionId: string, attempt = 1): Promise<HarnessSessionSnapshot> => {
        if (globalThis.navigator?.onLine === false) {
          await nextOnline()
        }

        try {
          return await client.readConversation(sessionId)
        } catch (error) {
          if (attempt >= READ_BACK_ATTEMPTS) {
            throw error
          }
          await new Promise((resolve) => setTimeout(resolve, READ_BACK_DELAY_MS))

          return readBack(sessionId, attempt + 1)
        }
      }

      /**
       * A cut turn stream is not a dead turn: the harness runs it detached from the request. So a
       * cut is answered by reading where the session stands and showing that — reattaching when the
       * turn is still running. Any other failure, or a session with no record to rebuild from, is
       * reported as it always was.
       */
      const reattachOnCut =
        (sessionId: string) =>
        (error: unknown): void => {
          const appVersionId = store.appVersionId()

          if (harnessErrorCode(error) !== HARNESS_ERROR_CODES.streamTruncated) {
            fail(error)

            return
          }

          const isLatestRequest = lifetime.latestRequestCheck()

          dropTurnStream()
          void readBack(sessionId)
            .then((snapshot) => {
              if (isLatestRequest() && store.sessionId() === sessionId) {
                if (snapshot.record) {
                  show(appVersionId, sessionId, snapshot)
                } else {
                  fail(error)
                }
              }
            })
            .catch(() => {
              if (isLatestRequest()) {
                fail(error)
              }
            })
        }

      /**
       * Follow a turn still running harness-side. The tail replays the turn from its start (or, from
       * another pod, rebuilds it from the stored transcript) before going live, so the session is put
       * back to where that turn began — up to its `user` event — and the turn folds in on top of it,
       * rather than twice.
       *
       * A relay without the route answers 404; the session is then read again until the turn has
       * ended, which the harness's lease bounds.
       */
      const tail = (
        appVersionId: string | null,
        sessionId: string,
        record: HarnessSessionRecord
      ): void => {
        const isLatestRequest = lifetime.latestRequestCheck()
        const scope = turnScope()

        if (!scope) {
          return
        }

        const turnStart = record.events.map((event) => event.event).lastIndexOf('user') + 1

        patchState(store, restoreSession({ ...record, events: record.events.slice(0, turnStart) }))
        consume(client.streamTurnTail(sessionId, 0, scope), (error: unknown) => {
          if (harnessErrorHttpStatus(error) === 404) {
            void awaitTurnEnd(appVersionId, sessionId, isLatestRequest)
          } else if (isLatestRequest()) {
            fail(error)
          }
        })
      }

      // ponytail: a fixed poll while the relay lacks turn/stream; drop it once the route is everywhere.
      const awaitTurnEnd = async (
        appVersionId: string | null,
        sessionId: string,
        isLatestRequest: () => boolean
      ): Promise<void> => {
        try {
          for (;;) {
            await new Promise((resolve) => setTimeout(resolve, TURN_POLL_MS))

            if (!isLatestRequest()) {
              return
            }

            const snapshot = await client.readConversation(sessionId)

            if (!isLatestRequest()) {
              return
            }

            if (snapshot.record?.status !== 'running') {
              show(appVersionId, sessionId, snapshot)

              return
            }
          }
        } catch (error) {
          if (isLatestRequest()) {
            fail(error)
          }
        }
      }

      /** Put a conversation on screen from its owner's read, replaying its whole record. */
      const show = (
        appVersionId: string | null,
        sessionId: string,
        snapshot: HarnessSessionSnapshot
      ): void => {
        const record = snapshot.record

        if (!record) {
          forgetConversation()

          return
        }

        const restored = restoreSession(record)

        rememberConversation(sessionId)
        feedback.replace(snapshot.feedback)
        conversationDetails.replace(snapshot.conversation ?? null)
        patchState(store, restored, {
          transcript: withMessageIds(restored.transcript, record.events),
          preview: null,
          executionBusy: false,
          executionError: null,
        })
        review.clear()

        // Only `running` is a live turn — never a transcript that merely lacks its `end`.
        if (record.status === 'running') {
          tail(appVersionId, sessionId, record)
        }
      }

      /** Leave the current session where it is and clear the screen for another. */
      /** Something is actually on the wire, and `clear()` would drop the subscription without
       * stopping what it is subscribed to: the harness keeps going with nothing on screen pointing
       * back at it. New and switching conversations are disabled on the same condition — this is
       * the backstop under that, so a caller reaching the store another way cannot walk away from a
       * live run either.
       *
       * The open subscription, not `streamState`: that is set optimistically when the message is
       * appended, before the session even exists, and abandoning a turn whose `openSession` is
       * still pending is the deliberate escape hatch that keeps a late session from attaching. A
       * finished stream leaves its subscription behind closed, so `closed` is the live part. */
      const live = (): boolean =>
        (!!turnStream && !turnStream.closed) || lifetime.isRunStreamOpen() || store.executionBusy()

      const clear = (appVersionId: string | null): void => {
        dropTurnStream()
        lifetime.leaveConversation()
        feedback.reset()
        conversationDetails.reset()
        patchState(store, initialHarnessSessionState, {
          ...initialScopeState,
          appVersionId,
        })
        review.clear()
      }

      /** A turn beside a run reads and explains, or queues a change for the next item boundary
       * (D14). The run has a stream of its own. */
      const sendTurn = async (prompt: string): Promise<void> => {
        const scope = turnScope()

        if (store.isStreaming() || store.offProjectPageReason() || !scope) {
          return
        }
        const context = client.captureMessageContext(scope)
        lifetime.invalidateRequestChecks()
        const isLatestRequest = lifetime.latestRequestCheck()
        patchState(store, (state) => ({
          ...appendUserMessage(state, prompt),
          preview: null,
          briefRevisionPending: false,
        }))

        try {
          const sessionId = await ensureSession(context)
          if (isLatestRequest()) {
            // A turn carries the page's headers, so no refusal of it names a project to retry under.
            consume(client.streamTurn(sessionId, prompt, context), onRefusal(sessionId))
          }
        } catch (error) {
          if (isLatestRequest()) {
            fail(error)
          }
        }
      }

      /** The read putting `sessionId` on screen is over, however it ended. Matched by id rather
       * than by request generation: a clear has already reset it, and a later open owns it now. */
      const finishOpening = (sessionId: string): void => {
        if (store.openingSessionId() === sessionId) {
          patchState(store, { openingSessionId: null })
        }
      }

      /** Put the tab's own conversation back after a reload, read as its owner: it is the maker's,
       * so it comes back wherever they are. Nothing is picked for them when the tab remembers
       * none — an empty conversation is where a new one starts. */
      const restoreConversation = async (): Promise<void> => {
        const sessionId = recallConversation()

        if (!sessionId) {
          return
        }

        const isLatestRequest = lifetime.latestRequestCheck()

        patchState(store, { openingSessionId: sessionId })

        try {
          const snapshot = await client.readConversation(sessionId)

          // Not over a conversation the maker started or opened in the meantime.
          if (!isLatestRequest() || store.sessionId() || store.streamState() !== 'idle') {
            return
          }

          show(store.appVersionId(), sessionId, snapshot)
        } catch {
          if (isLatestRequest()) {
            forgetConversation()
          }
        } finally {
          finishOpening(sessionId)
        }
      }

      // Whether this tab has put its conversation back yet: once, on arrival.
      let restored = false

      /** The maker moved, and the conversation goes with them. Nothing is cleared — the version
       * on screen is only where the next turn's context and unsaved-changes checks are read —
       * and the tab's own conversation is put back once, on arrival. */
      const arrive = (appVersionId: string | null): void => {
        if (appVersionId !== null && appVersionId !== store.appVersionId()) {
          patchState(store, { appVersionId })
        }

        if (!restored) {
          restored = true
          void restoreConversation()
        }
      }

      return {
        arrive,

        /** Bounded writes used by execution. Frames still fold transcript and run state together. */
        foldRunFrame: (frame: HarnessFrame, expectedSessionId?: string): void => {
          if (expectedSessionId !== undefined && expectedSessionId !== store.sessionId()) {
            return
          }
          patchState(store, (state) => applyRunFrame(state, frame))
        },
        applyRunCut: (): void => patchState(store, applyRunCut),
        applyRunFailure: (message: string): void =>
          patchState(store, (state) => applyRunFailure(state, message)),
        patchRunState: (
          patch: Partial<Pick<HarnessRunState, 'preview' | 'executionBusy' | 'executionError'>>
        ): void => patchState(store, patch),
        acceptExecutionPreview: (
          preview: HarnessExecutionPreview,
          expectedPlanVersion: number,
          reviewingRestart: boolean
        ): void => {
          if (store.planRecord()?.version !== expectedPlanVersion) {
            patchState(store, {
              executionBusy: false,
              preview: null,
              executionError: 'The plan changed while preparing the review. Review it again.',
            })
            return
          }
          patchState(store, (state) => acceptExecutionPreview(state, preview, reviewingRestart))
        },
        replaceRecord: (record: HarnessSessionRecord): void =>
          patchState(store, (state) => reconcileSessionRecord(state, record, true)),
        restoreSession: (record: HarnessSessionRecord): void =>
          patchState(store, restoreSession(record)),
        applyTurnFailure: fail,
        appendExecutionErrorNotice: (message: string): void =>
          patchState(store, (state) => withNotice(state, 'error', message)),
        rereadConversation,
        readBackAfterStreamCut: readBack,
        planCallScope,

        sendTurn,

        /** An interrupted turn, asked again as a new turn — never as the resume it may have been:
         * that gate is gone. The maker's last words are what is asked. */
        retry: (): void => {
          if (store.canRetry()) {
            void sendTurn(retryPrompt(store.transcript()))
          }
        },

        /** Prose to the agent: an answer to its question, or a change asked of its proposal. */
        respond: (message: string): void => resume('respond', message),

        /** An option picked on the agent's question. Same wire call as `respond`, but recorded as
         * a numbered check-line: the maker chose rather than wrote. */
        pick: (option: string): void => {
          const { question } = toQuestionDisplay(store.interrupt()?.payload)

          resume('respond', option, [{ text: option, question }])
        },

        /**
         * Confirm the description — in two presses when blockers are outstanding. Disabling
         * approval instead would make a proposal unapprovable in the common case, since nearly
         * every open question comes back blocking. This is the lifecycle's one approval: it pins a
         * committed baseline and has the harness prepare the work items, and authorises no write
         * and no branch — hence *confirmed*, not *approved*.
         */
        approve: (): void => {
          if (review.hasSteering() || !store.isApprovable()) {
            return
          }

          if (store.blockers() && !review.confirmingApproval()) {
            review.confirmApproval()

            return
          }

          // Recorded as the maker's turn: a transcript in which the agent simply proceeds reads
          // as the agent having decided. The words are the button's own. A conversation-owned
          // confirmation also says whether the agent may adjust the plan it prepares (D15).
          const confirmingBrief = store.interruptType() === 'brief_review'

          resume(
            'approve',
            undefined,
            confirmingBrief ? 'Gave the go-ahead.' : 'Approved.',
            confirmingBrief ? review.allowAdjustments() : undefined
          )
        },

        // Likewise on the record — a rejection that leaves no trace looks like the proposal
        // simply went away. The specialist gate can be declined; see `canReject`.
        //
        // `reason` is the specialist gate's optional word on why. It rides the wire as `message`,
        // which the harness reads into the reject decision, and is echoed so the maker's own
        // words stay in the conversation rather than only in the agent's context.
        reject: (reason?: string): void => {
          if (store.canReject()) {
            const said = reason?.trim()

            resume('reject', said || undefined, said ? `Declined. ${said}` : 'Declined.')
          }
        },

        /**
         * Allow a `read_grant`: this conversation reads the project the maker's page is in,
         * read-only, until it goes idle. The harness re-checks the maker's own access before it
         * grants anything, so this never widens what they can read. `coverage` is what the prompt
         * named, for the line the conversation keeps of the answer.
         */
        allowReadGrant: (coverage: string): void => {
          if (store.interruptType() !== 'read_grant') {
            return
          }

          resume('approve', undefined, `Allowed this conversation to read ${coverage}.`)
        },

        /** Decline a `read_grant`: the agent answers from the write target alone. */
        declineReadGrant: (coverage: string): void => {
          if (store.interruptType() !== 'read_grant') {
            return
          }

          resume('reject', undefined, `Did not allow reading ${coverage}.`)
        },

        /**
         * Send the accumulated steering as a `respond`.
         *
         * Never as an approve: the harness will not infer approval from steering, and neither
         * should the UI — a maker adjusting a decision is asking for a revision, not signing off.
         * What is staged and how it reads back belong to the review store; this only carries it.
         */
        sendSteering: (): void => resume('respond'),

        /** Take or change the maker's feedback on one agent message. */
        setMessageFeedback: (
          messageId: string,
          verdict: HarnessFeedbackVerdict,
          reasons: readonly string[],
          comment: string | null,
          clientContext: FeedbackClientContext
        ): void =>
          changeFeedback(messageId, { messageId, verdict, reasons, comment }, (sessionId) =>
            client.putMessageFeedback(sessionId, messageId, {
              verdict,
              reasons,
              comment,
              clientContext,
            })
          ),

        /** Clear it again. */
        clearMessageFeedback: (messageId: string): void =>
          changeFeedback(messageId, null, (sessionId) =>
            client.deleteMessageFeedback(sessionId, messageId)
          ),

        /** The maker's Cancel: drop the stream and close the turn as stopped, never as broken. */
        cancelTurn: (): void => {
          dropTurnStream()
          lifetime.invalidateRequestChecks()
          patchState(store, applyCancel)
        },

        /** Nothing is closed — the session stays in the harness's list — and the new one opens
         * lazily, on the first turn. */
        newSession: (): void => {
          if (live()) {
            return
          }

          const appVersionId = store.appVersionId()

          clear(appVersionId)
          forgetConversation()
        },

        /** Open another of the maker's conversations, from anywhere — read as its owner. */
        selectSession: async (sessionId: string): Promise<void> => {
          const appVersionId = store.appVersionId()

          if (sessionId === store.sessionId() || sessionId === store.openingSessionId() || live()) {
            return
          }

          clear(appVersionId)

          const isLatestRequest = lifetime.latestRequestCheck()

          patchState(store, { openingSessionId: sessionId })

          try {
            const snapshot = await client.readConversation(sessionId)

            if (isLatestRequest()) {
              show(appVersionId, sessionId, snapshot)
            }
          } catch (error) {
            if (isLatestRequest()) {
              fail(error)
            }
          } finally {
            finishOpening(sessionId)
          }
        },

        /** Open the branch the conversation writes to, at its head — in whatever project it is. */
        openWriteTarget: async (): Promise<void> => {
          const target = store.writeTarget()

          if (!target || conversationDetails.openingWriteTarget()) {
            return
          }

          conversationDetails.beginOpeningWriteTarget()

          try {
            const opened = await navigator.openWriteTarget(target)

            if (!opened) {
              patchState(store, {
                executionError:
                  'Navigation was cancelled. Resolve unsaved changes, then open the branch again.',
              })
            }
          } catch (error) {
            patchState(store, { executionError: harnessErrorMessage(error) })
          } finally {
            conversationDetails.finishOpeningWriteTarget()
          }
        },

        /** Stop this conversation reading another project. The harness drops what the grant read
         * from every later model call. The grant leaves the rail once the harness has taken the
         * revoke, so a failed read after it cannot leave it listed and revocable twice; that read
         * only reconciles. */
        revokeReadGrant: async (grantId: string): Promise<void> => {
          const sessionId = store.sessionId()

          if (!sessionId || conversationDetails.revokingReadGrantId()) {
            return
          }

          const isLatestRequest = lifetime.latestRequestCheck()

          // Until the revoke and the owner read after it are done, so a second press cannot send
          // the same revoke again.
          conversationDetails.beginRevokingReadGrant(grantId)

          try {
            await client.revokeGrant(sessionId, grantId)

            if (store.sessionId() === sessionId) {
              const revokedAt = new Date().toISOString()

              conversationDetails.markReadGrantRevoked(grantId, revokedAt)
            }

            if (isLatestRequest()) {
              await rereadConversation()
            }
          } catch (error) {
            if (isLatestRequest()) {
              patchState(store, (state) => withNotice(state, 'error', harnessErrorMessage(error)))
            }
          } finally {
            conversationDetails.finishRevokingReadGrant()
          }
        },
      }
    }
  ),
  withHooks({
    onInit(store, config = inject(HarnessConfigService)) {
      // The conversation follows the maker; the route is where the next turn is made from.
      effect(() => {
        const appVersionId = config.appVersionId() ?? null
        const workspaceId = config.workspaceId()

        // AI permissions can arrive after the route. Nothing is read until they allow it.
        if (!config.available() || (!appVersionId && !workspaceId)) {
          return
        }

        untracked(() => store.arrive(appVersionId))
      })

      // Kept with the canonical preview and refusal: this runs even before execution is first used.
      // A plan that takes the last one's place — the next one in the conversation after an abandon
      // or a finished run, a revision on the maker's word — is reviewed afresh: the last one's
      // preview and refusal are about a plan that is no longer on screen (conversation-owned).
      let planRevision = store.planRevision()

      effect(() => {
        const revision = store.planRevision()

        untracked(() => {
          if (revision !== planRevision && !store.executionBusy()) {
            patchState(store, { preview: null, executionError: null })
          }
          planRevision = revision
        })
      })
    },
  })
)

/** How often a session is re-read while its turn runs and the relay cannot tail it. */
const TURN_POLL_MS = 3000

/** How many times a cut stream's read-back is tried, and how long apart, before giving up. */
const READ_BACK_ATTEMPTS = 5
const READ_BACK_DELAY_MS = 2000

/** The browser's next `online` event. */
function nextOnline(): Promise<void> {
  return new Promise((resolve) =>
    globalThis.addEventListener('online', () => resolve(), { once: true })
  )
}

/** The conversation is the maker's and goes where they go. `sessionStorage` keeps each tab's
 * choice separate and avoids adopting a days-old id from an in-memory harness after a restart. */
const CONVERSATION_KEY = 'flx-harness-conversation'

function rememberConversation(sessionId: string): void {
  try {
    sessionStorage.setItem(CONVERSATION_KEY, sessionId)
  } catch {
    // A browser refusing storage costs recovery after a reload, nothing more.
  }
}

function recallConversation(): string | null {
  try {
    return sessionStorage.getItem(CONVERSATION_KEY)
  } catch {
    return null
  }
}

function forgetConversation(): void {
  try {
    sessionStorage.removeItem(CONVERSATION_KEY)
  } catch {
    // As above.
  }
}
